import { useIntl } from 'react-intl'
import { useState } from 'react'
import { Table } from 'react-bootstrap'
import { Accordion } from 'radix-ui'
import clsx from 'clsx'
import styles from '../../payment-tickets/components/FinishedTicketItem.module.css'
import { ClientCell } from '../../../common/ClientCell'
import { PayoutTokenLink } from '../../../common/PayoutTokenLink'

import { convertUTCtoMSK, formatSum, truncateByWords } from '../../../../utils'
import { IsGranted } from '../../auth/PrivateRoute'
import { ROLE, useAuth } from '../../auth'
import { IPayoutTicket, ITransaction } from '../payoutTickets.types'
import { payoutTypeLabel } from '../core/payoutRequisites'
import { TableHead } from '../../../../components/Table/TableHead'
import { KTIcon } from '../../../../_metronic/helpers'
import { getLatestTransaction } from '../utils/getLatestTransaction'
import { SmartTooltip } from '../../../../components/SmartTooltip/SmartTooltip'
import { AuditLogLink } from '../../Logs/routes/Audit/components/AuditLogLink'
import { CashBoxAttempts, CashBoxAttemptsToggle } from '../../ticket-card/components/CashBoxAttempts'
import { isCashBoxRow } from '../../ticket-card/core/cashBoxAttempts.types'

const TRANSACTION_COLUMNS = 9

export const TransactionRow = ({
  transaction,
  onCopy,
}: {
  transaction: ITransaction
  onCopy: (text: string) => void
}) => {
  const intl = useIntl()
  const [attemptsOpen, setAttemptsOpen] = useState(false)
  const hasAttempts = Boolean(transaction.cashBoxAttempts && transaction.cashBoxAttempts.length > 0)

  return (
    <>
      <tr>
        <td>
          {hasAttempts && (
            <CashBoxAttemptsToggle
              isOpen={attemptsOpen}
              onToggle={() => setAttemptsOpen((prev) => !prev)}
            />
          )}
        </td>
        <td
          className={transaction.paymentId && styles.transactionId}
          onClick={() => transaction.paymentId && onCopy(transaction.paymentId)}
        >
          {transaction.paymentId || '-'}
        </td>
        <td>{convertUTCtoMSK(transaction.createdAt)}</td>
        <td>
          {(transaction.completedAt && convertUTCtoMSK(transaction.completedAt)) || '-'}
        </td>
        <td>
          {intl.formatMessage({
            id: `transaction.status.${transaction.status}` || '-',
          })}
        </td>
        <td>{transaction.paymentAdapter || '-'}</td>
        <td>{transaction.error ? truncateByWords(transaction.error, 90) : '-'}</td>
        <td>{transaction.errorRaw || transaction.error ? (<>
          <SmartTooltip content={truncateByWords(transaction.errorRaw || transaction.error || '', 250)}>
            <span
              style={{
                display: 'inline-flex',
                minWidth: '25px',
                justifyContent: 'center',
                cursor: 'help',
                userSelect: 'none',
                padding: 0,
                border: '0px',
              }}
            >
              <KTIcon className={`fs-1 text-danger`} iconName='information-5' />
            </span>
          </SmartTooltip>
        </>): ''}</td>
        <td className='text-end'>
          {!isCashBoxRow(transaction) && <AuditLogLink paymentId={transaction.paymentId} />}
        </td>
      </tr>
      {hasAttempts && attemptsOpen && (
        <tr>
          <td colSpan={TRANSACTION_COLUMNS}>
            <CashBoxAttempts attempts={transaction.cashBoxAttempts} />
          </td>
        </tr>
      )}
    </>
  )
}

const FinishedTicketItem = ({ ticket }: { ticket: IPayoutTicket }) => {
  const intl = useIntl()
  const { currentUser } = useAuth()
  const [isOpen, setIsOpen] = useState(false)
  
  const hasTransactions = ticket.transactions && ticket.transactions.length > 0
  const latestTransaction = getLatestTransaction(ticket.transactions)

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text)
  }

  const toggleTransactions = () => {
    if (hasTransactions) {
      setIsOpen((prev) => !prev)
    }
  }

  return (
    <>
      <Accordion.Root
        type='single'
        collapsible
        asChild
        className={styles.AccordionRoot}
        onValueChange={(value) => {
          if (hasTransactions) {
            setIsOpen(() => Boolean(value))
          }
        }}
      >
        <Accordion.Item value='item-1' asChild className={styles.AccordionItem}>
          <tr
            className={clsx(hasTransactions && styles.pointer)}
          >
            {/* Добавляем новую колонку с указателем для разворачивания списка транзакций */}
            <td 
              className="text-center" 
              style={{ 
                width: '40px', 
                cursor: hasTransactions ? 'pointer' : 'default',
                verticalAlign: 'middle'
              }}
              onClick={toggleTransactions}
            >
              {hasTransactions && (
                <div className="bg-light-primary bg-opacity-20 rounded-circle p-2 d-inline-flex">
                  <KTIcon 
                    iconName={isOpen ? 'arrow-down' : 'arrow-right'} 
                    className='fs-2 text-primary'
                  />
                </div>
              )}
            </td>
            <td>
              <PayoutTokenLink token={ticket.token} />
            </td>
            <td>{convertUTCtoMSK(ticket.createdAt)}</td>
            <td>{payoutTypeLabel(ticket)}</td>
            <td>{ticket.completionDate ? convertUTCtoMSK(ticket.completionDate) : '-'}</td>
            <td>{ticket.amount ? formatSum(ticket.amount) : '-'}</td>
            <td>{ticket.requestedAmount ? formatSum(ticket.requestedAmount) : '-'}</td>
            <ClientCell
              externalId={ticket.externalUser?.externalId}
              token={ticket.externalUser?.token}
            />
            {!IsGranted([ROLE.ROLE_MATCHING_OPERATOR], currentUser) && (
              <td>{ticket.merchant.title}</td>
            )}
            <td>{latestTransaction?.paymentId || '-'}</td>
            <td>{latestTransaction?.paymentAdapter || '-'}</td>
            <td>{ticket.executor ? ticket.executor.uuid : '-'}</td>
            {IsGranted([ROLE.ROLE_ADMIN, ROLE.ROLE_MANAGER, ROLE.ROLE_MERCHANT_PAYMENTS_OPERATOR, ROLE.ROLE_MERCHANT_MANAGER], currentUser) && (
              <td>{ticket?.cashBox?.title || '-'}</td>
            )}
            <td>{intl.formatMessage({ id: 'ticket.status.' + ticket.status })}</td>
            <td className='text-end'>
              <AuditLogLink ticketToken={ticket.token} />
            </td>
          </tr>
        </Accordion.Item>
      </Accordion.Root>
      {isOpen && hasTransactions && (
        <tr>
          <td colSpan={15}>
            <Table bordered striped={false}>
              <TableHead>
                <th className='min-w-25px'></th>
                <th className='min-w-150px max-w-150px ps-3'>ID адаптера</th>
                <th className='min-w-140px'>Дата создания</th>
                <th className='min-w-140px'>Дата закрытия</th>
                <th className='min-w-100px'>Статус</th>
                <th className='min-w-120px'>Адаптер</th>
                <th>Причина отмены</th>
                <th className='min-w-25px'></th>
                <th className='min-w-50px'></th>
              </TableHead>
              {ticket.transactions && ticket.transactions
                .slice() // Создаем копию массива перед сортировкой
                .sort((a, b) => {
                  // Сортировка по createdAt в порядке возрастания (ASC)
                  return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
                })
                .map((transaction: ITransaction) => (
                  <TransactionRow
                    key={transaction.paymentId}
                    transaction={transaction}
                    onCopy={handleCopy}
                  />
                ))}
            </Table>
          </td>
        </tr>
      )}
    </>
  )
}

export default FinishedTicketItem 